import React, { useEffect, useMemo, useState } from 'react';
import { colorToCss, getGradeColorHexFromThresholds } from '../../Utils/gradePalette';
import { getLithColorHex } from '../../Utils/lithPalette';
import { useGradePaletteStore } from '../../store/gradePaletteStore';
import { getHoleRecord, subscribeHoleRecords } from '../../store/holeRecordStore';
import { isPlannedHole } from '../../Utils/holeTraceColor';
import { bestInterval, compositeAssays } from '../../Utils/holeComposite';

const CONNECT_EPS = 0.05;

function toDepth(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

function fmtDepth(value) {
  const n = toDepth(value);
  if (n == null) return '—';
  return n.toFixed(n % 1 === 0 ? 0 : 1);
}

function lithCss(code) {
  const hex = getLithColorHex(code);
  return `#${Number(hex).toString(16).padStart(6, '0')}`;
}

export function holeRecordFromDetails(details) {
  if (!details) return { record: null, holeId: '', dataset: '' };
  const holeId = String(details.Hole_ID || details.holeID || details.holeId || '').trim();
  let dataset = String(details.DataSet || '').trim();
  if (!dataset && details.holeKey) {
    const [ds] = String(details.holeKey).split('::');
    dataset = String(ds || '').trim();
  }
  const record = dataset && holeId ? getHoleRecord(dataset, holeId) : null;
  return { record, holeId, dataset };
}

function intervalRows(record, mode) {
  const raw = mode === 'lith' ? record?.geology || [] : record?.assays || [];
  return raw
    .map((row) => {
      const from = toDepth(row.Depth_From);
      const to = toDepth(row.Depth_To);
      if (from == null || to == null || to <= from) return null;
      if (mode === 'lith') {
        const code = String(row.Lith1_Code || '').trim();
        if (!code) return null;
        return { from, to, value: code, label: code };
      }
      const grade = parseFloat(row.Au_ppm);
      if (!Number.isFinite(grade)) return null;
      return { from, to, value: grade, label: `${grade.toFixed(2)} g/t` };
    })
    .filter(Boolean)
    .sort((a, b) => a.from - b.from || a.to - b.to);
}

/** Join touching/overlapping intervals that share the same lith code into one From–To. */
function mergeConnectingLith(rows) {
  if (!rows.length) return [];
  const out = [{ ...rows[0] }];
  for (let i = 1; i < rows.length; i += 1) {
    const prev = out[out.length - 1];
    const cur = rows[i];
    const same = String(prev.value).toUpperCase() === String(cur.value).toUpperCase();
    const connected = cur.from <= prev.to + CONNECT_EPS;
    if (same && connected) {
      prev.to = Math.max(prev.to, cur.to);
    } else {
      out.push({ ...cur });
    }
  }
  return out;
}

function copyText(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      resolve();
    } catch (err) {
      reject(err);
    }
  });
}

function copyLog(holeId, mode, rows) {
  const col = mode === 'lith' ? 'Lith' : 'Au_ppm';
  const lines = [['Hole_ID', 'From', 'To', 'Interval_Length', col].join('\t')];
  if (rows.length) {
    rows.forEach((row) => {
      lines.push(
        [holeId, fmtDepth(row.from), fmtDepth(row.to), fmtDepth(row.to - row.from), row.value].join('\t')
      );
    });
  } else {
    lines.push([holeId, '', '', '', ''].join('\t'));
  }
  return copyText(lines.join('\n'));
}

export default function DownholeLog({ details, compact = false, hideTitle = false }) {
  const thresholds = useGradePaletteStore((s) => s.thresholds);
  const [, setEpoch] = useState(0);
  const [copied, setCopied] = useState(false);
  useEffect(() => subscribeHoleRecords(() => setEpoch((n) => n + 1)), []);
  const { record, holeId } = holeRecordFromDetails(details);
  const planned = isPlannedHole(details) || isPlannedHole(record?.collar);
  const [mode, setMode] = useState('assay');
  const [compositeOn, setCompositeOn] = useState(false);

  const collar = record?.collar;
  const maxDepth = toDepth(collar?.Max_Depth);
  const best = useMemo(
    () => (mode === 'assay' && record?.assays?.length ? bestInterval(record.assays) : null),
    [mode, record]
  );
  const rows = useMemo(() => {
    if (mode === 'lith') return mergeConnectingLith(intervalRows(record, mode));
    if (compositeOn) {
      return compositeAssays(record?.assays || [], 1).map((row) => ({
        from: row.from,
        to: row.to,
        value: row.value,
        label: row.label,
      }));
    }
    return intervalRows(record, mode);
  }, [record, mode, compositeOn]);

  const colorOf = (row) => {
    if (mode === 'lith') return lithCss(row.value);
    return colorToCss(getGradeColorHexFromThresholds(row.value, thresholds));
  };

  const handleCopy = async () => {
    try {
      await copyLog(holeId, mode, rows);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  if (!holeId) return null;

  const loading =
    record &&
    ((mode === 'assay' && !record.hasAssays) || (mode === 'lith' && !record.hasGeology));

  return (
    <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
      {!hideTitle && (
        <div className="flex items-center gap-1">
          <p className="text-[9px] uppercase tracking-wider text-zinc-500 flex-1">Downhole log</p>
          {planned ? (
            <span className="text-[9px] uppercase tracking-wider text-zinc-400">Planned</span>
          ) : null}
        </div>
      )}
      <div className="flex items-center gap-1">
        <div className="inline-flex rounded-sm border border-yellow-600/30 overflow-hidden">
          <button
            type="button"
            className={`m-0 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
              mode === 'assay' ? 'bg-yellow-500/20 text-yellow-300' : 'text-zinc-500 hover:text-zinc-300'
            }`}
            onClick={() => setMode('assay')}
          >
            Assay
          </button>
          <button
            type="button"
            className={`m-0 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
              mode === 'lith' ? 'bg-emerald-500/20 text-emerald-300' : 'text-zinc-500 hover:text-zinc-300'
            }`}
            onClick={() => setMode('lith')}
          >
            Geology
          </button>
        </div>
        {mode === 'assay' ? (
          <button
            type="button"
            className={`m-0 h-5 px-1.5 rounded-sm border text-[9px] font-semibold uppercase tracking-wider ${
              compositeOn
                ? 'border-yellow-400 bg-yellow-500 text-black'
                : 'border-yellow-600/40 text-yellow-400 hover:bg-yellow-500/10'
            }`}
            onClick={() => setCompositeOn((v) => !v)}
            title="Length-weighted 1 m composites"
          >
            1 m
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleCopy}
          className="m-0 ml-auto h-5 px-1.5 rounded-sm border border-yellow-600/40 text-[9px] font-semibold uppercase tracking-wider text-yellow-400 hover:bg-yellow-500/10"
          title="Copy hole ID and intervals"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="text-[10px] text-zinc-400">
        {holeId}
        {maxDepth != null ? ` · TD ${fmtDepth(maxDepth)} m` : ''}
        {best ? ` · best ${best.label}` : ''}
      </p>
      {loading ? (
        <p className="text-[10px] text-zinc-500">Loading intervals…</p>
      ) : !rows.length ? (
        <p className="text-[10px] text-zinc-500">
          {planned
            ? 'Planned hole — no downhole intervals.'
            : mode === 'lith'
              ? 'No geology intervals on this hole.'
              : 'No assay intervals on this hole.'}
        </p>
      ) : (
        <div
          className={`overflow-y-auto pr-0.5 ${compact ? 'h-56' : 'h-[min(52vh,28rem)]'}`}
        >
          <div className="flex min-h-full items-stretch gap-1.5">
            <div className="w-3.5 shrink-0 flex flex-col self-stretch">
              <div className="h-6 shrink-0" aria-hidden />
              <div
                className="flex-1 min-h-0 flex flex-col rounded-full overflow-hidden border border-white/20"
                style={{
                  boxShadow: 'inset -3px 0 5px rgba(255,255,255,0.16), inset 4px 0 8px rgba(0,0,0,0.55)',
                }}
              >
                {rows.map((row, index) => (
                  <div
                    key={`hole-${row.from}-${row.to}-${row.label}-${index}`}
                    title={`${fmtDepth(row.from)}–${fmtDepth(row.to)} m ${row.label}`}
                    style={{
                      flexGrow: Math.max(row.to - row.from, 0.05),
                      flexShrink: 1,
                      flexBasis: '1.25rem',
                      minHeight: '1.25rem',
                      background: colorOf(row),
                    }}
                  />
                ))}
              </div>
            </div>
            <div className="min-w-0 flex-1 flex flex-col self-stretch">
              <div className="h-6 shrink-0 grid grid-cols-4 items-center px-1 text-[9px] uppercase tracking-wider text-zinc-500">
                <span>From</span>
                <span>To</span>
                <span title="Interval_Length">Interval</span>
                <span>{mode === 'lith' ? 'Lith' : 'Au'}</span>
              </div>
              <div className="flex-1 flex flex-col min-h-0">
                {rows.map((row, index) => (
                  <div
                    key={`row-${row.from}-${row.to}-${row.label}-${index}`}
                    className="grid grid-cols-4 items-center border-b border-zinc-900/80 px-1 text-[10px]"
                    style={{
                      flexGrow: Math.max(row.to - row.from, 0.05),
                      flexShrink: 1,
                      flexBasis: '1.25rem',
                      minHeight: '1.25rem',
                    }}
                  >
                    <span className="tabular-nums text-zinc-400">{fmtDepth(row.from)}</span>
                    <span className="tabular-nums text-zinc-400">{fmtDepth(row.to)}</span>
                    <span className="tabular-nums text-zinc-400">{fmtDepth(row.to - row.from)}</span>
                    <span className="truncate text-zinc-200">{row.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
